import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Save, Loader2, ShieldCheck, Wrench, FlaskConical, ArrowLeft, GitBranch, Plus, X } from "lucide-react";

const CATEGORY_ICON = { Security: ShieldCheck, Quality: Wrench, Testing: FlaskConical };

export default function PipelineBuilder() {
  const location = useLocation();
  const navigate = useNavigate();
  const repo = location.state?.repo;

  const [catalog, setCatalog] = useState({ tools: [], languages: [] });
  const [language, setLanguage] = useState("Generic");
  const [selected, setSelected] = useState([]);
  const [extra, setExtra] = useState("");
  const [yamlText, setYamlText] = useState("");
  const [source, setSource] = useState("");
  const [note, setNote] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [branch, setBranch] = useState(repo?.default_branch || "main");
  const [branches, setBranches] = useState([]);
  const [showNewBranch, setShowNewBranch] = useState(false);
  const [newBranchName, setNewBranchName] = useState("");
  const [creatingBranch, setCreatingBranch] = useState(false);

  useEffect(() => {
    if (!repo) { navigate("/app/repositories"); return; }
    api.get("/tools").then((r) => {
      setCatalog(r.data);
      if (repo.language && r.data.languages.includes(repo.language)) setLanguage(repo.language);
    });
    api.get("/repos/branches", { params: { provider: repo.provider, repo_full_name: repo.full_name } })
      .then((r) => setBranches(r.data.branches))
      .catch(() => setBranches([repo.default_branch]));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!repo) return;
    setAnalyzing(true);
    api.get("/repos/analyze", { params: { provider: repo.provider, repo_full_name: repo.full_name, branch } })
      .then((r) => {
        setAnalysis(r.data);
        setLanguage(r.data.language);
        setSelected(r.data.tools);
        if (r.data.existing_pipeline) {
          setYamlText(r.data.existing_pipeline.content);
          setSource("existing");
          setNote(`Pipeline existente carregada de ${r.data.existing_pipeline.path}. Edite direto ou gere com IA para melhorar.`);
        } else {
          setYamlText("");
          setSource("");
          setNote("");
        }
      })
      .catch(() => {})
      .finally(() => setAnalyzing(false));
  }, [branch]); // eslint-disable-line

  if (!repo) return null;

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const createBranch = async () => {
    const name = newBranchName.trim();
    if (!name) return;
    setCreatingBranch(true);
    try {
      await api.post("/repos/branches", {
        provider: repo.provider, repo_full_name: repo.full_name, new_branch: name, source_branch: branch,
      });
      setBranches((b) => [...b, name]);
      setBranch(name);
      setNewBranchName("");
      setShowNewBranch(false);
      toast.success(`Branch "${name}" criada`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setCreatingBranch(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/pipelines/generate", {
        provider: repo.provider, repo_full_name: repo.full_name, branch,
        language, tools: selected, extra, existing_yaml: analysis?.existing_pipeline?.content || null,
      });
      setYamlText(data.yaml);
      setSource(data.source);
      setNote(data.note);
      toast.success("Pipeline gerada");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/pipelines", {
        provider: repo.provider, repo_full_name: repo.full_name, repo_external_id: repo.external_id,
        branch, language, tools: selected, yaml_content: yamlText, source,
      });
      toast.success("Pipeline salva");
      navigate(`/app/pipelines/${data.id}`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const grouped = catalog.tools.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="p-8 fadeup">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-acid mb-4">
        <ArrowLeft size={14} /> Voltar
      </button>
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">{repo.provider}</div>
      <h1 className="text-4xl font-black tracking-tighter mb-1">{repo.name}</h1>
      <div className="text-sm text-zinc-500 mb-4">{repo.full_name}</div>

      <div className="flex items-center gap-2 flex-wrap mb-6">
        <GitBranch size={14} className="text-zinc-500" />
        <select data-testid="branch-select" value={branch} onChange={(e) => setBranch(e.target.value)}
          className="bg-void border border-line px-2 py-1.5 text-xs focus:border-acid focus:outline-none">
          {(branches.includes(branch) ? branches : [branch, ...branches]).map((b) => (
            <option key={b} value={b}>{b}{b === repo.default_branch ? " (padrão)" : ""}</option>
          ))}
        </select>
        {branch === repo.default_branch && (
          <span className="text-xs uppercase tracking-widest border border-acid text-acid px-2 py-0.5">atual</span>
        )}
        {showNewBranch ? (
          <>
            <input data-testid="new-branch-input" autoFocus value={newBranchName} onChange={(e) => setNewBranchName(e.target.value)}
              placeholder="nome-da-branch" onKeyDown={(e) => e.key === "Enter" && createBranch()}
              className="bg-void border border-line px-2 py-1.5 text-xs focus:border-acid focus:outline-none" />
            <button data-testid="create-branch-btn" onClick={createBranch} disabled={creatingBranch || !newBranchName.trim()}
              className="text-xs uppercase tracking-widest border border-acid text-acid px-2 py-1.5 hover:bg-acid hover:text-black transition-colors disabled:opacity-40">
              {creatingBranch ? "Criando..." : "Criar"}
            </button>
            <button onClick={() => { setShowNewBranch(false); setNewBranchName(""); }} className="text-zinc-500 hover:text-zinc-300">
              <X size={14} />
            </button>
          </>
        ) : (
          <button data-testid="new-branch-btn" onClick={() => setShowNewBranch(true)}
            className="flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-400 hover:text-acid transition-colors">
            <Plus size={12} /> nova branch
          </button>
        )}
      </div>

      {analyzing && (
        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-8"><Loader2 className="animate-spin" size={14} /> analisando repositório...</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Config */}
        <div className="space-y-6">
          <div className="border border-line bg-surface p-6">
            <label className="text-xs uppercase tracking-widest text-zinc-500">Linguagem principal</label>
            <select data-testid="language-select" value={language} onChange={(e) => setLanguage(e.target.value)}
              className="w-full mt-2 bg-void border border-line px-3 py-3 text-sm focus:border-acid focus:outline-none">
              {catalog.languages.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>

          <div className="border border-line bg-surface p-6">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Ferramentas open-source</div>
            <div className="space-y-5">
              {Object.entries(grouped).map(([cat, tools]) => {
                const Icon = CATEGORY_ICON[cat] || Wrench;
                return (
                  <div key={cat}>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-acid mb-2"><Icon size={14} /> {cat}</div>
                    <div className="grid grid-cols-1 gap-2">
                      {tools.map((t) => (
                        <button key={t.id} data-testid={`tool-${t.id}`} onClick={() => toggle(t.id)}
                          className={`text-left border px-4 py-3 transition-colors ${selected.includes(t.id) ? "border-acid bg-acid/5" : "border-line hover:border-zinc-500"}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-sm">{t.name}</span>
                            <span className={`w-4 h-4 border ${selected.includes(t.id) ? "bg-acid border-acid" : "border-zinc-600"}`} />
                          </div>
                          <div className="text-xs text-zinc-500 mt-1">{t.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border border-line bg-surface p-6">
            <label className="text-xs uppercase tracking-widest text-zinc-500">Requisitos adicionais (opcional)</label>
            <textarea data-testid="extra-requirements" value={extra} onChange={(e) => setExtra(e.target.value)} rows={3}
              placeholder="ex: build de imagem Docker e deploy em staging..."
              className="w-full mt-2 bg-void border border-line px-3 py-3 text-sm focus:border-acid focus:outline-none resize-none" />
          </div>

          <button data-testid="generate-btn" onClick={generate} disabled={generating}
            className="w-full bg-acid text-black font-bold uppercase tracking-widest text-sm py-4 flex items-center justify-center gap-2 hover:bg-[#32e612] transition-colors disabled:opacity-50">
            {generating
              ? <><Loader2 className="animate-spin" size={16} /> {analysis?.existing_pipeline ? "Melhorando com IA..." : "Gerando com IA..."}</>
              : <><Sparkles size={16} /> {analysis?.existing_pipeline ? "Melhorar pipeline com IA" : "Gerar pipeline com IA"}</>}
          </button>
        </div>

        {/* Preview */}
        <div className="border border-line bg-surface flex flex-col">
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <span className="text-xs uppercase tracking-widest text-zinc-500">
              {analysis?.existing_pipeline?.path || { gitlab: ".gitlab-ci.yml", azure: "azure-pipelines.yml" }[repo.provider] || ".github/workflows/pipeforge.yml"}
            </span>
          </div>
          <textarea data-testid="yaml-editor" value={yamlText} onChange={(e) => setYamlText(e.target.value)}
            placeholder="# a configuração aparecerá aqui..."
            spellCheck={false}
            className="flex-1 min-h-[420px] bg-void text-acid/90 text-xs leading-relaxed p-4 focus:outline-none resize-none font-mono" />
          {note && <div className="text-xs text-zinc-500 px-4 py-2 border-t border-line">{note}</div>}
          <div className="border-t border-line p-4">
            <button data-testid="save-pipeline-btn" onClick={save} disabled={!yamlText || saving}
              className="w-full border border-acid text-acid font-bold uppercase tracking-widest text-sm py-3 flex items-center justify-center gap-2 hover:bg-acid hover:text-black transition-colors disabled:opacity-40">
              {saving ? <><Loader2 className="animate-spin" size={16} /> Salvando...</> : <><Save size={16} /> Salvar pipeline</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
